/**
 * "Which paper is this?" — the one place the three session kinds become a
 * label. A session sits either a generated mock exam, an archived past exam
 * (`pastExam: "HT24"`) or one of the newly written practice papers
 * (`practicePaper: "PE-03"`), and the lobby history, the result page and the
 * grading page all have to say which.
 *
 * Kept pure and separate from the pages so the mapping is unit-tested
 * directly rather than only through a rendered table cell.
 */
import type { TFunction } from "@client/i18n";
import type { ExamKind } from "./types";

/** The minimum a row needs for a label — `SessionDto` and `HistoryEntryDto` both satisfy it. */
export interface PaperIdentity {
  kind: ExamKind;
  pastExam: string | null;
  practicePaper: string | null;
}

/**
 * "Tenta HT24" for an archived paper, "Övningstenta PE-03" for a practice paper,
 * `null` for a generated one (which has no paper to name).
 */
export function paperLabel(entry: PaperIdentity, t: TFunction): string | null {
  if (entry.kind === "past-exam" && entry.pastExam) {
    return t("exam.lobby.startConfirm.pastExam", { term: entry.pastExam });
  }
  if (entry.kind === "practice-paper" && entry.practicePaper) {
    return t("exam.lobby.history.practicePaper", { paper: entry.practicePaper });
  }
  return null;
}

/** The same label, with "Slumpad övningstenta / Generated" standing in for a generated session. */
export function paperLabelOrGenerated(entry: PaperIdentity, t: TFunction): string {
  return paperLabel(entry, t) ?? t("exam.lobby.history.generated");
}
